import {memo} from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import MUIContainer from '@/components/MUI-Components/MUI-Container';
import {colors, fontFamily, fontSize, fontWeight, letterSpacing, lineHeightRatio, typography} from '@/styles/tokens';
import {sectionHeadingSx} from '@/styles/sectionHeading';

export interface RnChallenge {
    /** Small uppercase kicker above the challenge, e.g. "Release Velocity". */
    label: string;
    title: string;
    description: string;
}

interface RnChallengesProps {
    heading: string;
    subheading?: string;
    challenges: RnChallenge[];
}

// Header: heading on the left, subheading on the right, bottom-aligned. Stacks on phones.
const headerSx = {
    display: 'grid',
    gridTemplateColumns: {xs: '1fr', md: 'minmax(0, 7fr) minmax(0, 5fr)'},
    alignItems: 'end',
    gap: {xs: 1.5, md: 6},
    mb: {xs: 4, md: 5}
} as const;

// Same heading/subheading treatment as SectionHeader, so it matches the page's other sections.
const headingSx = {...sectionHeadingSx, color: colors.text.primary} as const;

const subheadingSx = {color: colors.text.primary} as const;

const listSx = {listStyle: 'none', p: 0, m: 0, borderTop: `1px solid ${colors.neutral.borderLight}`} as const;

const ACCENT_LINE_WIDTH = '7rem';

// One ruled row per challenge: number, kicker + title, impact. On hover the row
// lifts to white, the number turns orange, and an accent line draws in underneath.
const rowSx = {
    position: 'relative',
    display: 'grid',
    gridTemplateColumns: {xs: 'auto minmax(0, 1fr)', md: '4rem minmax(0, 6fr) minmax(0, 5fr)'},
    alignItems: 'center',
    columnGap: {xs: 2, md: 3},
    rowGap: 1,
    py: {xs: 2.5, md: 3},
    px: {xs: 1, md: 1.5},
    borderBottom: `1px solid ${colors.neutral.borderLight}`,
    transition: 'background-color 0.25s ease',
    '&::after': {
        content: '""',
        position: 'absolute',
        left: 0,
        bottom: -1,
        height: 2,
        width: 0,
        background: `linear-gradient(90deg, ${colors.accent.orange}, ${colors.primary.deep})`,
        transition: 'width 0.35s ease'
    },
    '&:hover': {bgcolor: colors.background.default},
    '&:hover::after': {width: ACCENT_LINE_WIDTH},
    '&:hover .rn-challenge-number': {color: colors.text.orangeAccessible},
    '@media (prefers-reduced-motion: reduce)': {transition: 'none', '&::after': {transition: 'none'}}
} as const;

const numberSx = {
    fontFamily: fontFamily.mono,
    fontSize: fontSize[12],
    color: colors.text.muted,
    transition: 'color 0.25s ease',
    alignSelf: {xs: 'start', md: 'center'},
    pt: {xs: 0.25, md: 0}
} as const;

const labelSx = {
    fontFamily: fontFamily.mono,
    fontSize: fontSize[11],
    fontWeight: fontWeight.medium,
    color: colors.text.muted,
    textTransform: 'uppercase',
    letterSpacing: letterSpacing.eyebrow,
    mb: 0.75
} as const;

const titleSx = {
    color: colors.text.primary,
    fontWeight: fontWeight.bold,
    fontSize: {xs: fontSize[18], md: fontSize[20]},
    lineHeight: lineHeightRatio.snug
} as const;

// On phones the impact sits under the title, in the second column.
const descSx = {...typography.bodySm, color: colors.text.primary, gridColumn: {xs: '2', md: 'auto'}} as const;

const RnChallenges = ({heading, subheading, challenges}: RnChallengesProps) => (
    <MUIContainer>
        <Box sx={headerSx}>
            <Typography variant="h1" component="h2" className="font-headers" sx={headingSx}>
                {heading}
            </Typography>
            {subheading && (
                <Typography variant="subtitle1" component="p" className="font-regular-text" sx={subheadingSx}>
                    {subheading}
                </Typography>
            )}
        </Box>
        <Box component="ol" sx={listSx}>
            {challenges.map((challenge, idx) => (
                <Box component="li" key={challenge.title} sx={rowSx}>
                    <Typography component="span" className="rn-challenge-number" sx={numberSx} aria-hidden="true">
                        {String(idx + 1).padStart(2, '0')}
                    </Typography>
                    <Box>
                        <Typography component="p" sx={labelSx}>
                            {challenge.label}
                        </Typography>
                        <Typography component="h3" className="font-headers" sx={titleSx}>
                            {challenge.title}
                        </Typography>
                    </Box>
                    <Typography component="p" className="font-regular-text" sx={descSx}>
                        {challenge.description}
                    </Typography>
                </Box>
            ))}
        </Box>
    </MUIContainer>
);

export default memo(RnChallenges);
